/**
 * Full-screen sheet — a variant of the existing #modalbg/#modal pair, not a
 * second overlay system. Opening a sheet adds the `sheet` class to #modal and
 * reuses the same backdrop, so all three dismissal paths (✕ button, backdrop
 * click, Escape) keep working with zero new wiring: they all end in
 * closeModal(), which calls back into runSheetCleanups() here.
 *
 * Cleanup registers through an ARRAY, deliberately not another single slot
 * like modal.ts's `_dayMapDispose` (which daymap owns): a sheet can mount
 * several disposable things at once — a Leaflet map, a ResizeObserver, a
 * matchMedia listener — and each registers itself independently.
 */

const cleanups: (() => void)[] = [];

/** Register a disposer to run when the current sheet closes (or is replaced). */
export function addSheetCleanup(fn: () => void): void {
  cleanups.push(fn);
}

/**
 * The modal shell lives in renderShell(), which only the trip page calls —
 * so on /admin, /new and friends there was no #modal to open and a sheet
 * threw on a null element. Creating it on demand makes sheets work on any
 * page, and wires the backdrop-click dismissal that main.ts can only attach
 * at boot to a shell that already exists.
 */
function ensureModalShell(): HTMLElement {
  const existing = document.getElementById("modal");
  if (existing) return existing;
  const bg = document.createElement("div");
  bg.className = "modal-bg";
  bg.id = "modalbg";
  const modal = document.createElement("div");
  modal.className = "modal";
  modal.id = "modal";
  bg.appendChild(modal);
  document.body.appendChild(bg);
  bg.addEventListener("click", (e) => {
    if ((e.target as HTMLElement).id === "modalbg") {
      bg.classList.remove("open");
      runSheetCleanups();
    }
  });
  return modal;
}

/**
 * Mark the shared modal as a full-screen sheet and open it. Returns the
 * #modal element for the caller to fill. Any previous sheet's cleanups run
 * first, so sheet-over-sheet can't leak.
 */
export function openSheet(): HTMLElement {
  runSheetCleanups();
  const modal = ensureModalShell();
  modal.classList.add("sheet");
  modal.style.maxWidth = "";
  document.getElementById("modalbg")!.classList.add("open");
  return modal;
}

/**
 * Run and drop every registered cleanup, and shed the sheet styling.
 * Called by closeModal() on every dismissal path; safe to call when no
 * sheet is open (the array is simply empty).
 */
export function runSheetCleanups(): void {
  while (cleanups.length) {
    try {
      cleanups.pop()!();
    } catch {
      /* one broken disposer must not strand the rest */
    }
  }
  document.getElementById("modal")?.classList.remove("sheet");
}
